import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "@tanstack/react-router"
import { signUp } from "aws-amplify/auth"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import type { DataRegion, RegionDetails } from "@/types/auth"
import { validateRegionDetails } from "@/utils/region"
import { EmailOTPVerification } from "./email-otp-verification"
import { DataRegionField } from "./forms/fields/auth/data-region"
import { LegalLinks } from "./legal-links"
import { Alert, AlertDescription } from "./ui/alert"
import { Button } from "./ui/button"
import { Checkbox } from "./ui/checkbox"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form"
import { Input } from "./ui/input"

interface SignUpFormProps {
  redirect?: string
  regionDetails?: RegionDetails
}

export function SignUpForm({ redirect, regionDetails }: SignUpFormProps = {}) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<"signup" | "verify">("signup")
  const [email, setEmail] = useState("")

  const signUpSchema = z.object({
    email: z.string().email({ message: t("auth.errors.invalidEmail") }),
    firstName: z.string().min(1, { message: t("auth.errors.required") }),
    lastName: z.string().min(1, { message: t("auth.errors.required") }),
    dataRegion: z.enum(["us", "ca"]),
    isProfessional: z.boolean(),
  })

  // Validate region from regionDetails - only use if it's a valid region
  const validatedRegion = validateRegionDetails(regionDetails)

  const form = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      dataRegion: validatedRegion as DataRegion,
      isProfessional: false,
    },
  })

  async function onSubmit(values: z.infer<typeof signUpSchema>) {
    setIsLoading(true)
    setError(null)

    try {
      const { nextStep: signUpNextStep } = await signUp({
        username: values.email,
        options: {
          userAttributes: {
            email: values.email,
            given_name: values.firstName,
            family_name: values.lastName,
            "custom:isProfessional": values.isProfessional.toString(),
            "custom:hasOnboarded": "false",
          },
          autoSignIn: {
            authFlowType: "USER_AUTH",
          },
        },
      })

      if (signUpNextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setEmail(values.email)
        setStep("verify")
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "An error occurred during sign up")
    } finally {
      setIsLoading(false)
    }
  }

  if (step === "verify") {
    return (
      <EmailOTPVerification
        email={email}
        mode="signup"
        onBack={() => setStep("signup")}
        isLoading={isLoading}
      />
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="dataRegion"
          render={({ field }) => (
            <DataRegionField field={field} regionDetails={regionDetails} mode="signup" />
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.signUp.firstNameLabel")}</FormLabel>
                <FormControl>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder={t("auth.signUp.firstNamePlaceholder")}
                    required
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.signUp.lastNameLabel")}</FormLabel>
                <FormControl>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder={t("auth.signUp.lastNamePlaceholder")}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.common.emailLabel")}</FormLabel>
              <FormControl>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.common.emailPlaceholder")}
                  required
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isProfessional"
          render={() => (
            <FormItem>
              <FormLabel>{t("auth.signUp.isProfessionalLabel")}</FormLabel>
              <FormDescription>{t("auth.signUp.isProfessionalDescription")}</FormDescription>
              <FormField
                control={form.control}
                name="isProfessional"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormControl>
                      <Checkbox
                        id="isProfessional"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal">
                      {t("auth.signUp.isProfessionalLabel")}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t("auth.common.loading") : t("auth.signUp.signUpButton")}
        </Button>

        {/* TODO: Add Google Sign In */}

        {/* <p className="text-center text-sm text-muted-foreground">{t("auth.common.or")}</p>

        <Button variant="outline" className="w-full" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
              fill="currentColor"
            />
          </svg>
          {t("auth.signUp.googleButton")}
        </Button> */}

        <div className="text-center text-sm text-muted-foreground">
          {t("auth.signUp.existingAccountText")}{" "}
          <Link
            to="/sign-in"
            search={redirect ? { redirect } : {}}
            className="text-primary hover:underline underline-offset-4"
          >
            {t("auth.signUp.signInLink")}
          </Link>
        </div>

        <LegalLinks
          termsText={t("auth.common.termsText")}
          termsOfService={t("common.termsOfService")}
          privacyPolicy={t("common.privacyPolicy")}
          dataDeletionAgreement={t("common.dataDeletionAgreement")}
          andText={t("auth.common.and")}
        />
      </form>
    </Form>
  )
}
